import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Home, Key, Building, Calculator, CheckCircle, Percent, Shield, Clock, ArrowRight } from 'lucide-react';
import { services } from '../mockData';
import { useLanguage } from '../context/LanguageContext';

export const Services = () => {
  const { isArabic } = useLanguage();
  const iconMap = {
    Home: Home,
    Key: Key,
    Building: Building,
    Calculator: Calculator
  };

  return (
    <div className="min-h-screen pt-20 pb-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-16 fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-[#404040] mb-4">
            Our Services
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Comprehensive estate agency services designed to make your property journey smooth and successful
          </p>
        </div>

        {/* Services Grid */}
        <div className="space-y-16">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div key={service.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}>
                <div className="lg:w-1/2">
                  <Card className="card-hover border-none shadow-xl p-8">
                    <div className="feature-icon mb-6">
                      <IconComponent className="w-10 h-10" />
                    </div>
                    <h2 className="text-3xl font-bold text-[#404040] mb-4">{service.title}</h2>
                    <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                    
                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-[#FF8C42] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                <div className="lg:w-1/2">
                  <img
                    src={
                      service.id === 1 
                        ? 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80'
                        : service.id === 2
                        ? 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&q=80'
                        : service.id === 3
                        ? 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80'
                        : 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80'
                    }
                    alt={service.title}
                    className="rounded-lg shadow-lg w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-20 bg-gray-50 rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-[#404040] mb-8 text-center">
            {isArabic ? 'لماذا تختار هاري وجاكوبس؟' : 'Why Choose Harry & Jacobs?'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FF8C42] mb-3">97%</div>
              <h3 className="text-xl font-semibold text-[#404040] mb-2">
                {isArabic ? 'من سعر الطلب' : 'Of Asking Price'}
              </h3>
              <p className="text-gray-600">
                {isArabic ? 'نحقق باستمرار نتائج ممتازة للبائعين' : 'We consistently achieve excellent results for our sellers'}
              </p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FF8C42] mb-3">92%</div>
              <h3 className="text-xl font-semibold text-[#404040] mb-2">
                {isArabic ? 'تجديد المستأجرين' : 'Tenant Renewal'}
              </h3>
              <p className="text-gray-600">
                {isArabic ? 'معدل رضا عالي بين الملاك والمستأجرين' : 'High satisfaction rate among our landlords and tenants'}
              </p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FF8C42] mb-3">15+</div>
              <h3 className="text-xl font-semibold text-[#404040] mb-2">
                {isArabic ? 'سنوات الخبرة' : 'Years Experience'}
              </h3>
              <p className="text-gray-600">
                {isArabic ? 'معرفة عميقة بسوق عقارات هارو' : 'Deep knowledge of the Harrow property market'}
              </p>
            </div>
          </div>
        </div>

        {/* Full Management Service - 12% Fee */}
        <div className="mt-16" dir={isArabic ? 'rtl' : 'ltr'}>
          <Card className="border-none shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] p-8 text-white">
              <div className={`flex flex-col lg:flex-row items-center justify-between gap-8 ${isArabic ? 'lg:flex-row-reverse' : ''}`}>
                <div className="flex-1">
                  <div className={`flex items-center gap-4 mb-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                      <Percent className="w-10 h-10" />
                    </div>
                    <div>
                      <h2 className="text-4xl font-bold">12%</h2>
                      <p className="text-xl opacity-90">
                        {isArabic ? 'رسوم الإدارة الشاملة' : 'Full Management Fee'}
                      </p>
                    </div>
                  </div>
                  <p className="text-lg opacity-90 mb-6">
                    {isArabic 
                      ? 'من إجمالي الإيجار السنوي - نحن نتولى كل شيء حتى تتمكن من الاسترخاء'
                      : 'Of annual rent value - We handle everything so you can relax'
                    }
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className={`flex items-center gap-3 ${isArabic ? 'flex-row-reverse' : ''}`}>
                      <Shield className="w-6 h-6" />
                      <span>{isArabic ? 'فحص شامل للمستأجرين' : 'Full Tenant Vetting'}</span>
                    </div>
                    <div className={`flex items-center gap-3 ${isArabic ? 'flex-row-reverse' : ''}`}>
                      <Clock className="w-6 h-6" />
                      <span>{isArabic ? 'إدارة على مدار الساعة' : '24/7 Management'}</span>
                    </div>
                    <div className={`flex items-center gap-3 ${isArabic ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle className="w-6 h-6" />
                      <span>{isArabic ? 'جمع الإيجار المضمون' : 'Guaranteed Rent Collection'}</span>
                    </div>
                  </div>

                  <Link to="/landlord/enquiry">
                    <Button size="lg" className="bg-white text-[#FF8C42] hover:bg-gray-100">
                      {isArabic ? 'استفسر الآن' : 'Enquire Now'}
                      <ArrowRight className={`w-5 h-5 ${isArabic ? 'mr-2 rotate-180' : 'ml-2'}`} />
                    </Button>
                  </Link>
                </div>
                
                <div className="flex-1 max-w-md">
                  <Card className="bg-white/10 backdrop-blur border-white/20">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-4">
                        {isArabic ? 'ما يشمله' : "What's Included"}
                      </h3>
                      <ul className="space-y-3">
                        {[
                          isArabic ? 'التسويق والإعلان' : 'Marketing & Advertising',
                          isArabic ? 'فحص المرجعيات' : 'Reference Checks',
                          isArabic ? 'إعداد عقد الإيجار' : 'Tenancy Agreement Preparation',
                          isArabic ? 'حماية الوديعة' : 'Deposit Protection',
                          isArabic ? 'جمع الإيجار' : 'Rent Collection',
                          isArabic ? 'تنسيق الصيانة' : 'Maintenance Coordination',
                          isArabic ? 'عمليات التفتيش المنتظمة' : 'Regular Inspections',
                          isArabic ? 'إدارة الخروج' : 'Check-out Management'
                        ].map((item, idx) => (
                          <li key={idx} className={`flex items-center gap-2 ${isArabic ? 'flex-row-reverse' : ''}`}>
                            <CheckCircle className="w-4 h-4 text-white" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-[#404040] rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Contact us today to discuss how we can help with your property needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="inline-block">
              <button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white px-8 py-4 rounded-lg font-semibold text-lg btn-hover-lift">
                Contact Us
              </button>
            </a>
            <a href="/valuation" className="inline-block">
              <button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#404040] px-8 py-4 rounded-lg font-semibold text-lg transition-all">
                Free Valuation
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
